import React, { useState, forwardRef } from 'react';
import { cx } from "@/utils/cx";
import { Icons } from "@/assets";

export interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    isError?: boolean;
    rightElement?: React.ReactNode; // For Password Toggle or Error Icon
    containerClassName?: string;
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
    ({ type = "text", className, isError, rightElement, containerClassName, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);
        const isPasswordType = type === 'password';
        const inputType = isPasswordType ? (showPassword ? 'text' : 'password') : type;

        return (
            <div className={cx("flex flex-col gap-3", containerClassName)}>
                <div
                    className={cx(
                        "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out group",
                        isError
                            ? "bg-[#ff424f]"
                            : "bg-[#d1d5dc] focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]"
                    )}
                >
                    <div className="relative flex items-center bg-white rounded-[8.5px] overflow-hidden">
                        <input
                            ref={ref}
                            type={inputType}
                            className={cx(
                                "w-full h-[51px] px-4 text-gray-100 text-sm placeholder:text-gray-80 outline-none",
                                className,
                                (rightElement || isError || isPasswordType) && "pr-10" // Add padding if icon exists
                            )}
                            {...props}
                        />

                        {/* Password Toggle Logic */}
                        {isPasswordType && !isError && !rightElement && (
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 text-sm text-[#64646D] hover:text-gray-100"
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        )}

                        {/* External Right Element (e.g. Upload Button in Doc Field) */}
                        {rightElement && (
                            <div className="absolute right-4">
                                {rightElement}
                            </div>
                        )}

                        {/* Error Icon (Highest Priority) */}
                        {isError && (
                            <div className="absolute right-4 pointer-events-none">
                                <Icons.ErrorIcon />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);
